import { LogOut, Maximize2, Monitor, Moon, Radar, Search, Settings, Sun } from 'lucide-react'
import { CapturePanel } from '@/components/panels/CapturePanel'
import { ConnectionStatus } from '@/components/panels/ConnectionStatus'
import { FloorSwitcher, type FloorOption } from '@/components/panels/FloorSwitcher'
import { JourneyPanel, type JourneyPanelProps } from '@/components/panels/JourneyPanel'
import { RelayStatus } from '@/components/panels/RelayStatus'
import { SectionTitle } from '@/components/panels/SectionTitle'
import { UserDetail } from '@/components/panels/UserDetail'
import { UserList } from '@/components/panels/UserList'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { CaptureModeId, CaptureProgress } from '@/lib/capture'
import { CAPTURE_ENABLED } from '@/lib/config'
import type { ThemeChoice } from '@/hooks/useTheme'
import type {
  BlueiotStatus,
  ConnectionStatus as Status,
  LiveUser,
  SessionInfo,
} from '@/types/liveview'

/** The visibility switches, so `App` and the sidebar cannot drift apart. */
export type ToggleKey =
  | 'showOffline'
  | 'showTrails'
  | 'showAccuracy'
  | 'showPhones'
  | 'showTags'
  | 'showJourneys'

const THEME_ICON: Record<ThemeChoice, typeof Sun> = {
  dark: Moon,
  light: Sun,
  system: Monitor,
}

interface ToggleRowProps {
  id: string
  label: string
  hint?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

function ToggleRow({ id, label, hint, checked, onCheckedChange }: ToggleRowProps) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center justify-between gap-3 py-1.5">
      <span className="min-w-0">
        <span className="block text-xs font-medium">{label}</span>
        {hint && <span className="text-muted-foreground block text-[11px]">{hint}</span>}
      </span>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </label>
  )
}

/**
 * What the map draws, behind one button.
 *
 * The six switches used to sit inline above the journeys and pushed everything
 * that changes minute to minute below the fold; they are set once per sitting.
 * The expand button that sat here "did nothing" as far as anybody could tell —
 * it is disabled while nothing is on the map, which is most of the time a venue
 * is being set up — so framing the devices is now an item in the same menu,
 * still there for the moment it is useful.
 */
function MapSettings(props: SidebarProps) {
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon-sm" aria-label="Map settings">
              <Settings />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Map settings</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="flex flex-col">
          <ToggleRow
            id="show-offline"
            label="Show stale devices"
            hint="Kept until the server forgets them"
            checked={props.showOffline}
            onCheckedChange={(value) => props.onToggle('showOffline', value)}
          />
          <ToggleRow
            id="show-trails"
            label="Movement trails"
            checked={props.showTrails}
            onCheckedChange={(value) => props.onToggle('showTrails', value)}
          />
          <ToggleRow
            id="show-accuracy"
            label="Accuracy radius"
            checked={props.showAccuracy}
            onCheckedChange={(value) => props.onToggle('showAccuracy', value)}
          />
          <Separator className="my-1.5" />
          {/* Three kinds of dot can share this map, and they mean quite
              different things. Being able to see one kind at a time is how you
              tell a tag's position from the phone's opinion of it. */}
          <ToggleRow
            id="show-phones"
            label="Phones"
            hint="Devices posting their own fixes"
            checked={props.showPhones}
            onCheckedChange={(value) => props.onToggle('showPhones', value)}
          />
          <ToggleRow
            id="show-tags"
            label="BlueIOT tags"
            hint="Read from the venue's relay"
            checked={props.showTags}
            onCheckedChange={(value) => props.onToggle('showTags', value)}
          />
          <ToggleRow
            id="show-journeys"
            label="Journeys"
            hint="Positions LiveView is playing"
            checked={props.showJourneys}
            onCheckedChange={(value) => props.onToggle('showJourneys', value)}
          />
          <Separator className="my-1.5" />
          <Button
            variant="ghost"
            size="sm"
            className="justify-start px-1.5"
            onClick={props.onFitToDevices}
            disabled={props.visibleCount === 0}
          >
            <Maximize2 />
            Frame all visible devices
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export interface SidebarProps {
  status: Status
  onReconnect: () => void
  /** The API's BlueIOT relay, or `null` when this organisation has none. */
  relay: BlueiotStatus | null
  /** Whose data this is. Shown because a person can belong to more than one. */
  session: SessionInfo
  onLogout: () => void
  floorOptions: FloorOption[]
  level: number | null
  onLevelChange: (level: number) => void
  users: LiveUser[]
  onlineCount: number
  selectedUser: LiveUser | null
  selectedUserId: string | null
  onSelectUser: (userId: string | null) => void
  onLocate: (user: LiveUser) => void
  onForget: (userId: string) => void
  now: number
  showOffline: boolean
  showTrails: boolean
  showAccuracy: boolean
  showPhones: boolean
  showTags: boolean
  showJourneys: boolean
  onToggle: (key: ToggleKey, value: boolean) => void
  query: string
  onQueryChange: (query: string) => void
  onFitToDevices: () => void
  /** Devices the map is drawing right now — what "Frame all" would frame. */
  visibleCount: number
  following: boolean
  onToggleFollow: () => void
  theme: ThemeChoice
  onCycleTheme: () => void
  /** Devices hidden by the current floor or filters, for the list's footer. */
  hiddenCount: number
  captureModeId: CaptureModeId
  onCaptureModeChange: (modeId: CaptureModeId) => void
  surveyorId: string | null
  onSurveyorChange: (userId: string | null) => void
  captureProgress: CaptureProgress | null
  onCaptureReset: () => void
  /** Everything the Journeys section needs, passed through whole. */
  journeys: JourneyPanelProps
}

export function Sidebar(props: SidebarProps) {
  return (
    <aside className="bg-card/60 flex h-full w-[336px] shrink-0 flex-col border-r backdrop-blur-xl">
      <header className="flex items-center gap-2 px-4 py-3">
        <Radar className="text-primary size-4" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold tracking-tight">
            {props.session.organization.name ?? 'Proximi.io LiveView'}
          </h1>
          <p className="text-muted-foreground text-[11px]">
            {props.onlineCount} online · {props.users.length} tracked
          </p>
        </div>
        <RelayStatus status={props.relay} />
        <ConnectionStatus status={props.status} onReconnect={props.onReconnect} />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={props.onCycleTheme}
              aria-label={`Theme: ${props.theme}. Click to change.`}
            >
              {(() => {
                const Icon = THEME_ICON[props.theme]
                return <Icon />
              })()}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Theme: {props.theme}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={props.onLogout} aria-label="Sign out">
              <LogOut />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Sign out{props.session.user.email ? ` (${props.session.user.email})` : ''}
          </TooltipContent>
        </Tooltip>
      </header>

      <Separator />

      {/* Everything between the header and the pinned detail scrolls as one
          column. Only the device list used to, and once the journeys panel
          grew past the viewport it squeezed that list to nothing and the rest
          simply overflowed with no way to reach it. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex flex-col gap-2 px-3 py-3">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <FloorSwitcher
              options={props.floorOptions}
              level={props.level}
              onLevelChange={props.onLevelChange}
            />
          </div>
          <MapSettings {...props} />
        </div>

        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
          <Input
            value={props.query}
            onChange={(event) => props.onQueryChange(event.target.value)}
            placeholder="Filter devices…"
            aria-label="Filter devices by name or id"
            className="pl-8"
          />
        </div>
      </div>

      <Separator />

      <JourneyPanel {...props.journeys} />

      <Separator />

      {CAPTURE_ENABLED && (
        <>
          <CapturePanel
            modeId={props.captureModeId}
            onModeChange={props.onCaptureModeChange}
            users={props.users}
            surveyorId={props.surveyorId}
            onSurveyorChange={props.onSurveyorChange}
            progress={props.captureProgress}
            onReset={props.onCaptureReset}
          />

          <Separator />
        </>
      )}

      <SectionTitle
        className="px-3 pt-3"
        aside={<span className="tabular-nums">{props.users.length || null}</span>}
      >
        Current visitors
      </SectionTitle>

      <UserList
        users={props.users}
        selectedUserId={props.selectedUserId}
        currentLevel={props.level}
        now={props.now}
        filtered={props.query.trim().length > 0}
        onSelect={props.onSelectUser}
        onLocate={props.onLocate}
      />

      {props.hiddenCount > 0 && (
        <p className="text-muted-foreground border-t px-3 py-2 text-[11px]">
          {props.hiddenCount} {props.hiddenCount === 1 ? 'device is' : 'devices are'} hidden by the
          current filters.
        </p>
      )}
      </div>

      {props.selectedUser && (
        <UserDetail
          user={props.selectedUser}
          now={props.now}
          following={props.following}
          onToggleFollow={props.onToggleFollow}
          onClose={() => props.onSelectUser(null)}
          onForget={props.onForget}
        />
      )}
    </aside>
  )
}
